import { useMemo } from 'react';
import { ConfigurationCanvasPanelHeader } from '../../pages/configuration-canvas-panel/ConfigurationCanvasPanelHeader';
import { Breadcrumbs } from './Breadcrumbs';
import { useSelector } from 'react-redux';
import {
  selectSingleObject,
  selectSingleObjectActiveTab,
} from '../../../reducers/documentation/documentationReducer';
import { DocumentationTabTypes } from '../../../types/enums/DocumentationTabTypes';
import { useObjectListInteractions } from '../object-list/useObjectListInteractions';
import { AddObjectButton, AddObjectButtonVersion } from '../object-list/AddObjectButton';
import { useDocumentation } from '../useDocumentation';
import { useDocumentationContext } from '../context/DocumentationContext';

interface SelectedObjectHeaderProps {
  hideAddObjectButton?: boolean;
  isExpandedMode?: boolean;
  onCloseDialog?: () => void;
}

export const SelectedObjectHeader = ({
  hideAddObjectButton,
  isExpandedMode,
  onCloseDialog,
}: SelectedObjectHeaderProps) => {
  const objectTypeName = useSelector(selectSingleObject);
  const { objectNamesUsedInCanvas } = useDocumentationContext();
  const activeTab = useSelector(selectSingleObjectActiveTab);
  const isObjectCardActiveTab =
    (activeTab && activeTab === DocumentationTabTypes.CARDS_LIST) || isExpandedMode;

  const { backToObjectList, backToElementsList } = useObjectListInteractions();

  const { objectsBeingParsed } = useDocumentation();

  const baseBreadcrumbs = useMemo(
    () => [
      {
        value: 'Documentation',
        label: 'Documentation',
        onClick: backToObjectList,
      },
      {
        label: objectTypeName?.label ?? '',
        value: objectTypeName?.objectType ?? '',
        onClick: backToElementsList,
      },
    ],
    [objectTypeName, backToElementsList, backToObjectList],
  );

  const isLoading = objectsBeingParsed?.[objectTypeName?.objectType ?? ''];
  const breadcrumbList = useMemo(
    () =>
      activeTab && !isObjectCardActiveTab
        ? [...baseBreadcrumbs, { label: activeTab, value: activeTab, onClick: () => {} }]
        : baseBreadcrumbs,
    [activeTab, isObjectCardActiveTab, baseBreadcrumbs],
  );

  if (!objectTypeName) return null;

  return (
    <ConfigurationCanvasPanelHeader
      onClose={onCloseDialog}
      customButton={
        !hideAddObjectButton ? (
          <AddObjectButton
            isLoading={isLoading}
            objectName={objectTypeName.objectType}
            buttonVersion={AddObjectButtonVersion.small}
            isInFunnelMap={objectNamesUsedInCanvas?.includes(objectTypeName.objectType)}
          />
        ) : undefined
      }
      sxProps={{
        padding: '16px 16px 0',
      }}
    >
      <Breadcrumbs breadcrumbList={breadcrumbList} disableOverflowCalculations={isExpandedMode} />
    </ConfigurationCanvasPanelHeader>
  );
};
